// Main panel of the Product Attributes dashboard: the values table for the
// selected type — usage counts, per-value category-bindings chips (rendered
// straight from `value.boundCategoryIds`, no cache needed), CRUD, and
// drag-reorder. Row rendering lives in attribute-value-row.tsx; this table
// owns the dnd-kit DndContext/SortableContext plumbing and the optimistic
// reorder — see `SortableAttributeValueRow` there for the per-row hook.
//
// This is the ONLY place values are ever draggable: search results
// (attribute-search-results.tsx) and the category lens preview render a plain
// (non-sortable) row instead, which is how drag-reorder ends up "disabled
// while searching or lens active" per the plan, without a separate flag.

import { Icon } from '@chakra-ui/react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  IconButtonWithTooltip,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@elkaso-app/web-design';
import { useGetDealCategoryTreeApi } from 'apis/exclusive-deals';
import {
  AttributeTypeAdmin,
  AttributeUsage,
  AttributeValueAdmin,
  useDeleteAttributeValueApi,
  useReorderAttributeValuesApi,
} from 'apis/product-attributes';
import { useEffect, useState } from 'react';
import { FiPlus } from 'react-icons/fi';

import { SortableAttributeValueRow } from './attribute-value-row';
import { CategoryBindingsModal } from './category-bindings-modal';
import { ValueFormModal } from './value-form-modal';

interface Props {
  type: AttributeTypeAdmin;
  usage?: AttributeUsage;
}

const idsOf = (values: AttributeValueAdmin[]) => values.map((v) => v.id).join(',');

export const AttributeValuesTable = ({ type, usage }: Props) => {
  const toast = useToast();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editValue, setEditValue] = useState<AttributeValueAdmin | null>(null);
  const [deleteValue, setDeleteValue] = useState<AttributeValueAdmin | null>(null);
  const [bindingsValue, setBindingsValue] = useState<AttributeValueAdmin | null>(null);

  // Optimistic local order while a drag-reorder mutation is in flight — see
  // the effects below for when it's seeded/cleared.
  const [localOrder, setLocalOrder] = useState<AttributeValueAdmin[] | null>(null);

  const { data: treeData } = useGetDealCategoryTreeApi();
  const categoryTree = treeData?.data ?? [];

  const deleteApi = useDeleteAttributeValueApi({
    onSuccess: () => {
      toast({ title: `Attribute value "${deleteValue?.label}" deleted`, status: 'success' });
      setDeleteValue(null);
    },
    onError: (error: any) => {
      // hooks toast server messages; this is the network-error fallback
      if (!error?.response?.data?.message) toast({ title: 'Failed to delete attribute value.', status: 'error' });
    },
  });

  const reorderApi = useReorderAttributeValuesApi({
    onError: (error: any) => {
      setLocalOrder(null); // revert the optimistic order — server state wins
      if (!error?.response?.data?.message) toast({ title: 'Failed to reorder attribute values', status: 'error' });
    },
  });

  // Reset the optimistic override whenever the selected type changes.
  useEffect(() => {
    setLocalOrder(null);
  }, [type.id]);

  // Once the server catches up (post-invalidate refetch reflects the same
  // order we optimistically applied), drop the override so the table goes
  // back to being fully prop-driven.
  useEffect(() => {
    if (localOrder && idsOf(localOrder) === idsOf(type.values)) setLocalOrder(null);
  }, [type.values, localOrder]);

  const values = localOrder ?? type.values;

  const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}), useSensor(KeyboardSensor, {}));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = values.findIndex((v) => v.id === active.id);
    const newIndex = values.findIndex((v) => v.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(values, oldIndex, newIndex);
    setLocalOrder(reordered);
    reorderApi.mutate({ typeId: type.id, valueIds: reordered.map((v) => v.id) });
  };

  return (
    <Box>
      <Flex align='center' justify='space-between' mb='3' gap='3' wrap='wrap'>
        <Text fontWeight='semibold' fontSize='md'>
          Values — {type.label}
        </Text>
        <Flex align='center' gap='2'>
          <IconButtonWithTooltip
            aria-label='Add Value'
            label='Add Value'
            Icon={<Icon as={FiPlus} />}
            onClick={() => setIsCreateOpen(true)}
          />
        </Flex>
      </Flex>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}>
        <Table size='sm'>
          <Thead>
            <Tr>
              <Th w='1' aria-label='Drag handle column' />
              <Th>Label</Th>
              <Th>Value</Th>
              <Th>Synonyms</Th>
              <Th>Categories</Th>
              <Th>Usage</Th>
              <Th>Active</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <SortableContext items={values.map((v) => v.id)} strategy={verticalListSortingStrategy}>
            <Tbody>
              {values.map((value) => (
                <SortableAttributeValueRow
                  key={value.id}
                  value={value}
                  typeKey={type.key}
                  usage={usage}
                  categoryTree={categoryTree}
                  onOpenBindings={() => setBindingsValue(value)}
                  onEdit={() => setEditValue(value)}
                  onDelete={() => setDeleteValue(value)}
                />
              ))}
              {values.length === 0 && (
                <Tr>
                  <Td colSpan={8}>
                    <Text color='gray.500' fontSize='sm' textAlign='center' py='4'>
                      No values yet.
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </SortableContext>
        </Table>
      </DndContext>

      <ValueFormModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} typeId={type.id} />
      {editValue && (
        <ValueFormModal isOpen={!!editValue} onClose={() => setEditValue(null)} typeId={type.id} value={editValue} />
      )}
      {bindingsValue && (
        <CategoryBindingsModal
          isOpen={!!bindingsValue}
          onClose={() => setBindingsValue(null)}
          kind='value'
          targetId={bindingsValue.id}
          targetLabel={bindingsValue.label}
        />
      )}

      <Modal isOpen={!!deleteValue} onClose={() => setDeleteValue(null)} size='md' closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Attribute Value</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status='warning'>
              <AlertIcon />
              This action cannot be undone. Are you sure you want to delete “{deleteValue?.label}”?
            </Alert>
          </ModalBody>
          <ModalFooter gap='2'>
            <Button variant='outline' onClick={() => setDeleteValue(null)} isDisabled={deleteApi.isLoading}>
              Cancel
            </Button>
            <Button
              colorScheme='red'
              onClick={() => deleteValue && deleteApi.mutate({ id: deleteValue.id })}
              isLoading={deleteApi.isLoading}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
